// modules/editor_formatting.js
import { EditorSelection } from "@codemirror/state"
import { indentWithTab } from "@codemirror/commands"

export const createFormattingCommand = (prefix, suffix) => {
  return (view) => {
    const transaction = view.state.changeByRange(range => {
      const text = view.state.doc.sliceString(range.from, range.to)
      const isAlreadyFormatted = text.startsWith(prefix) && text.endsWith(suffix)

      if (isAlreadyFormatted) {
        return {
          changes: {
            from: range.from,
            to: range.to,
            insert: text.slice(prefix.length, -suffix.length)
          },
          range: EditorSelection.range(
            range.from,
            range.to - (prefix.length + suffix.length)
          )
        }
      } else if (range.empty) {
        const insert = `${prefix}${suffix}`
        return {
          changes: { from: range.from, insert },
          range: EditorSelection.cursor(range.from + prefix.length)
        }
      } else {
        return {
          changes: {
            from: range.from,
            to: range.to,
            insert: `${prefix}${text}${suffix}`
          },
          range: EditorSelection.range(
            range.from + prefix.length,
            range.to + prefix.length
          )
        }
      }
    })

    view.dispatch(transaction)
    return true
  }
}

export const indentListItem = (view) => {
  const state = view.state
  const changes = state.changeByRange(range => {
    const line = state.doc.lineAt(range.from)
    const lineText = line.text

    const listMatch = lineText.match(/^(\s*)([-*]|\d+\.)\s/)
    if (!listMatch) {
      return indentWithTab(view) ? { range } : null
    }

    const indent = "  "
    const newText = indent + lineText
    return {
      changes: {
        from: line.from,
        to: line.to,
        insert: newText
      },
      range: EditorSelection.range(range.from + indent.length, range.to + indent.length)
    }
  })

  if (changes.changes.empty) return false
  view.dispatch(changes)
  return true
}

export const unindentListItem = (view) => {
  const state = view.state
  const changes = state.changeByRange(range => {
    const line = state.doc.lineAt(range.from)
    const lineText = line.text

    const listMatch = lineText.match(/^(\s{2,})([-*]|\d+\.)\s/)
    if (!listMatch) return { range }

    const newText = lineText.replace(/^  /, "")
    return {
      changes: {
        from: line.from,
        to: line.to,
        insert: newText
      },
      range: EditorSelection.range(range.from - 2, Math.max(range.to - 2, line.from))
    }
  })

  if (changes.changes.empty) return false
  view.dispatch(changes)
  return true
}

export const continueList = (view) => {
  const state = view.state
  const changes = state.changeByRange(range => {
    const line = state.doc.lineAt(range.from)
    const lineText = line.text

    // Match list items (e.g., "- ", "* ", "1. ")
    const listMatch = lineText.match(/^(\s*)([-*]|\d+\.)\s/)
    if (!listMatch) return { range } // Let default Enter handle non-list lines

    const [_, indent, marker] = listMatch
    const isEmpty = lineText.trim() === `${marker}` || lineText.trim() === `${marker} `

    if (isEmpty) {
      // Empty list item: remove it and move to new line
      return {
        changes: {
          from: line.from,
          to: line.to,
          insert: ""
        },
        range: EditorSelection.cursor(line.from)
      }
    }

    // Continue list with single newline
    const newLine = `\n${indent}${marker} `
    return {
      changes: {
        from: range.from,
        insert: newLine
      },
      range: EditorSelection.cursor(range.from + newLine.length)
    }
  })

  view.dispatch(changes)
  return true // Always handle Enter ourselves
}

export const createLinkCommand = (view) => {
  const transaction = view.state.changeByRange(range => {
    const text = view.state.doc.sliceString(range.from, range.to)

    if (range.empty) {
      // No selection, insert empty link template
      const insert = "[]()";
      return {
        changes: { from: range.from, insert },
        range: EditorSelection.cursor(range.from + 1) // Position cursor inside brackets
      }
    } else {
      // Wrap selected text in link format
      return {
        changes: {
          from: range.from,
          to: range.to,
          insert: `[${text}]()`
        },
        range: EditorSelection.cursor(range.to + 3) // Position cursor inside parentheses
      }
    }
  })

  view.dispatch(transaction)
  return true
}

export const formattingKeymap = [
  { key: "Tab", run: indentListItem },
  { key: "Shift-Tab", run: unindentListItem },
  { key: "Enter", run: continueList, preventDefault: true },
  { key: "Cmd-b", mac: "Cmd-b", run: createFormattingCommand("**", "**"), preventDefault: true },
  { key: "Ctrl-b", run: createFormattingCommand("**", "**") },
  { key: "Cmd-i", mac: "Cmd-i", run: createFormattingCommand("*", "*"), preventDefault: true },
  { key: "Ctrl-i", run: createFormattingCommand("*", "*") },
  { key: "Cmd-e", mac: "Cmd-e", run: createFormattingCommand("_", "_"), preventDefault: true },
  { key: "Ctrl-e", run: createFormattingCommand("_", "_") },
  { key: "Cmd-`", mac: "Cmd-`", run: createFormattingCommand("`", "`"), preventDefault: true },
  { key: "Ctrl-`", run: createFormattingCommand("`", "`") },
  { key: "Cmd-k", mac: "Cmd-k", run: createLinkCommand, preventDefault: true },
  { key: "Ctrl-k", run: createLinkCommand },
]
